import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiSave, FiArrowLeft, FiUpload, FiX, FiImage, FiPackage, FiLink, FiMail, FiCode, FiTag } from 'react-icons/fi';
import { toast } from 'react-toastify';
import axios from 'axios';

const CATEGORIES = [
  { value: 'productivity', label: 'Productivity' },
  { value: 'privacy', label: 'Privacy & Security' },
  { value: 'social', label: 'Social' },
  { value: 'entertainment', label: 'Entertainment' },
  { value: 'developer-tools', label: 'Developer Tools' },
  { value: 'news', label: 'News' },
  { value: 'shopping', label: 'Shopping' },
  { value: 'accessibility', label: 'Accessibility' },
  { value: 'communication', label: 'Communication' },
  { value: 'utilities', label: 'Utilities' },
  { value: 'other', label: 'Other' }
];

function EditExtension() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [extension, setExtension] = useState(null);

  // Form fields
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [longDescription, setLongDescription] = useState('');
  const [version, setVersion] = useState('1.0.0');
  const [category, setCategory] = useState('utilities');
  const [tags, setTags] = useState('');
  const [website, setWebsite] = useState('');
  const [sourceCode, setSourceCode] = useState('');
  const [supportEmail, setSupportEmail] = useState('');
  const [changelog, setChangelog] = useState('');

  // Files
  const [icon, setIcon] = useState(null);
  const [iconPreview, setIconPreview] = useState('');
  const [screenshots, setScreenshots] = useState([]);
  const [screenshotPreviews, setScreenshotPreviews] = useState([]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      toast.error('Please login first');
      navigate('/login');
      return;
    }
    fetchExtension(token);
  }, [id, navigate]);

  const fetchExtension = async (token) => {
    try {
      const response = await axios.get(`https://extension.avalw.com/api/extensions/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const ext = response.data.data;

      if (ext.status !== 'draft') {
        toast.error('Only draft extensions can be edited');
        navigate('/dashboard');
        return;
      }

      setExtension(ext);
      setName(ext.name || '');
      setDescription(ext.description || '');
      setLongDescription(ext.longDescription || '');
      setVersion(ext.version || '1.0.0');
      setCategory(ext.category || 'utilities');
      setTags((ext.tags || []).join(', '));
      setWebsite(ext.website || '');
      setSourceCode(ext.sourceCode || '');
      setSupportEmail(ext.supportEmail || '');
      setChangelog(ext.changelog || '');

      if (ext.icon) {
        setIconPreview(ext.icon);
      }
      if (ext.screenshots && ext.screenshots.length > 0) {
        setScreenshotPreviews(ext.screenshots);
      }
    } catch (error) {
      console.error('Failed to fetch extension:', error);
      toast.error('Failed to load extension');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleIconChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        toast.error('Icon must be less than 2MB');
        return;
      }
      setIcon(file);
      setIconPreview(URL.createObjectURL(file));
    }
  };

  const handleScreenshotsChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length + screenshotPreviews.length > 5) {
      toast.error('Maximum 5 screenshots allowed');
      return;
    }

    const validFiles = files.filter(file => {
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`${file.name} is too large (max 5MB)`);
        return false;
      }
      return true;
    });

    setScreenshots(prev => [...prev, ...validFiles]);
    setScreenshotPreviews(prev => [...prev, ...validFiles.map(f => URL.createObjectURL(f))]);
  };

  const removeScreenshot = (index) => {
    setScreenshots(prev => prev.filter((_, i) => i !== index));
    setScreenshotPreviews(prev => prev.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      toast.error('Please login first');
      return;
    }

    if (!name.trim()) {
      toast.error('Name is required');
      return;
    }

    setSaving(true);
    try {
      const formData = new FormData();
      formData.append('name', name);
      formData.append('description', description);
      formData.append('longDescription', longDescription);
      formData.append('version', version);
      formData.append('category', category);
      formData.append('tags', JSON.stringify(tags.split(',').map(t => t.trim()).filter(t => t)));
      formData.append('website', website);
      formData.append('sourceCode', sourceCode);
      formData.append('supportEmail', supportEmail);
      formData.append('changelog', changelog);

      if (icon) {
        formData.append('icon', icon);
      }

      screenshots.forEach((file, index) => {
        formData.append(`screenshots`, file);
      });

      // Keep existing screenshots that weren't removed
      const existingScreenshots = screenshotPreviews.filter(url => !url.startsWith('blob:'));
      formData.append('existingScreenshots', JSON.stringify(existingScreenshots));

      await axios.put(`https://extension.avalw.com/api/extensions/${id}`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success('Extension updated successfully!');
      navigate('/dashboard');
    } catch (error) {
      console.error('Save error:', error);
      toast.error(error.response?.data?.message || 'Failed to save extension');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-gray-600">Loading extension...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="container mx-auto px-6 max-w-4xl">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/dashboard')}
              className="p-2 hover:bg-gray-200 rounded-lg transition-all"
            >
              <FiArrowLeft className="text-xl" />
            </button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Edit Extension</h1>
              <p className="text-gray-600">Update your extension details before submitting</p>
            </div>
          </div>
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-6 py-3 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-all font-medium disabled:opacity-50"
          >
            <FiSave />
            {saving ? 'Saving...' : 'Save Changes'}
          </motion.button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Form */}
          <div className="lg:col-span-2 space-y-6">
            {/* Basic Info */}
            <div className="bg-white rounded-2xl p-6 border-2 border-gray-200">
              <h2 className="text-xl font-bold mb-6 text-gray-900 flex items-center gap-2">
                <FiPackage /> Basic Information
              </h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Extension Name *</label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-gray-900 focus:outline-none transition-all"
                    placeholder="My Awesome Extension"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Short Description *</label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={3}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-gray-900 focus:outline-none transition-all resize-none"
                    placeholder="Brief description of what your extension does..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Detailed Description</label>
                  <textarea
                    value={longDescription}
                    onChange={(e) => setLongDescription(e.target.value)}
                    rows={6}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-gray-900 focus:outline-none transition-all resize-none"
                    placeholder="Full description with features, usage instructions, etc. Supports markdown..."
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Version</label>
                    <input
                      type="text"
                      value={version}
                      onChange={(e) => setVersion(e.target.value)}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-gray-900 focus:outline-none transition-all"
                      placeholder="1.0.0"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                    <select
                      value={category}
                      onChange={(e) => setCategory(e.target.value)}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-gray-900 focus:outline-none transition-all"
                    >
                      {CATEGORIES.map(cat => (
                        <option key={cat.value} value={cat.value}>{cat.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <FiTag className="inline mr-1" /> Tags (comma separated)
                  </label>
                  <input
                    type="text"
                    value={tags}
                    onChange={(e) => setTags(e.target.value)}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-gray-900 focus:outline-none transition-all"
                    placeholder="productivity, dark mode, tools"
                  />
                </div>
              </div>
            </div>

            {/* Links */}
            <div className="bg-white rounded-2xl p-6 border-2 border-gray-200">
              <h2 className="text-xl font-bold mb-6 text-gray-900 flex items-center gap-2">
                <FiLink /> Links & Support
              </h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Website URL</label>
                  <input
                    type="url"
                    value={website}
                    onChange={(e) => setWebsite(e.target.value)}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-gray-900 focus:outline-none transition-all"
                    placeholder="https://myextension.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <FiCode className="inline mr-1" /> Source Code URL
                  </label>
                  <input
                    type="url"
                    value={sourceCode}
                    onChange={(e) => setSourceCode(e.target.value)}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-gray-900 focus:outline-none transition-all"
                    placeholder="https://github.com/user/extension"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <FiMail className="inline mr-1" /> Support Email
                  </label>
                  <input
                    type="email"
                    value={supportEmail}
                    onChange={(e) => setSupportEmail(e.target.value)}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-gray-900 focus:outline-none transition-all"
                    placeholder="support@myextension.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Changelog</label>
                  <textarea
                    value={changelog}
                    onChange={(e) => setChangelog(e.target.value)}
                    rows={4}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-gray-900 focus:outline-none transition-all resize-none"
                    placeholder="v1.0.0 - Initial release&#10;- Feature 1&#10;- Feature 2"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar - Images */}
          <div className="space-y-6">
            {/* Icon Upload */}
            <div className="bg-white rounded-2xl p-6 border-2 border-gray-200">
              <h2 className="text-xl font-bold mb-4 text-gray-900">Extension Icon</h2>
              <p className="text-sm text-gray-600 mb-4">Square image, recommended 128x128px</p>

              <div className="flex flex-col items-center">
                {iconPreview ? (
                  <div className="relative mb-4">
                    <img
                      src={iconPreview}
                      alt="Icon preview"
                      className="w-32 h-32 rounded-2xl object-cover border-2 border-gray-200"
                    />
                    <button
                      onClick={() => { setIcon(null); setIconPreview(''); }}
                      className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                    >
                      <FiX className="text-sm" />
                    </button>
                  </div>
                ) : (
                  <div className="w-32 h-32 border-2 border-dashed border-gray-300 rounded-2xl flex items-center justify-center mb-4">
                    <FiImage className="text-4xl text-gray-400" />
                  </div>
                )}

                <label className="cursor-pointer px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-all text-sm font-medium">
                  <FiUpload className="inline mr-2" />
                  Upload Icon
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleIconChange}
                    className="hidden"
                  />
                </label>
              </div>
            </div>

            {/* Screenshots Upload */}
            <div className="bg-white rounded-2xl p-6 border-2 border-gray-200">
              <h2 className="text-xl font-bold mb-4 text-gray-900">Screenshots</h2>
              <p className="text-sm text-gray-600 mb-4">Up to 5 images showing your extension in action</p>

              <div className="space-y-3">
                {screenshotPreviews.map((url, index) => (
                  <div key={index} className="relative">
                    <img
                      src={url}
                      alt={`Screenshot ${index + 1}`}
                      className="w-full h-32 object-cover rounded-lg border-2 border-gray-200"
                    />
                    <button
                      onClick={() => removeScreenshot(index)}
                      className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                    >
                      <FiX className="text-sm" />
                    </button>
                  </div>
                ))}

                {screenshotPreviews.length < 5 && (
                  <label className="cursor-pointer block w-full py-8 border-2 border-dashed border-gray-300 rounded-lg text-center hover:border-gray-400 transition-all">
                    <FiUpload className="text-2xl text-gray-400 mx-auto mb-2" />
                    <span className="text-sm text-gray-600">Add Screenshot</span>
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleScreenshotsChange}
                      className="hidden"
                    />
                  </label>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default EditExtension;
